import { Box, Flex, Text } from '@elkaso-app/web-design';
import { formatPrice, freshness } from 'pages/prices/utils/matrix-display';
import { historyDiscountPct, SupplierPriceEntry, VariantPricing } from 'pages/sales-lookup/utils/headline';
import { vatCaption, VatMode, withVat } from 'pages/sales-lookup/utils/vat';
import { tierUnit, UomLadder } from 'utils/price-units';
import { useState } from 'react';

export interface SupplierPriceListProps {
  pricing: VariantPricing;
  ladder: UomLadder;
  vatMode: VatMode;
  highlightRestaurantId?: number;
}

// Ranked supplier rows. A row's number is the comparable price (converted to
// the comparison tier); rows that can't convert show their raw price + unit
// and never claim BEST. Tapping a row with history expands the per-restaurant
// closed table.
export const SupplierPriceList = ({ pricing, ladder, vatMode, highlightRestaurantId }: SupplierPriceListProps) => {
  const [expandedSupplierId, setExpandedSupplierId] = useState<number | null>(null);

  if (!pricing.rows.length) return null;

  return (
    <Box mt='16px'>
      <Text fontSize='10.5px' fontWeight='semibold' letterSpacing='0.1em' color='gray.500' mb='8px'>
        ALL SUPPLIERS · {vatCaption(vatMode).toUpperCase()} / {pricing.comparisonUnit.toUpperCase()}
      </Text>

      {pricing.rows.map((row) => {
        const isExpanded = row.supplierId === expandedSupplierId;
        return (
          <Box
            key={row.supplierId}
            border='1px solid'
            borderColor={isExpanded ? 'red.200' : 'gray.100'}
            borderRadius='12px'
            mb='8px'
            overflow='hidden'>
            <SupplierRowHeader
              row={row}
              vatMode={vatMode}
              onToggle={() => setExpandedSupplierId(isExpanded ? null : row.supplierId)}
            />
            {isExpanded && row.history.length > 0 && (
              <HistoryTable row={row} ladder={ladder} vatMode={vatMode} highlightRestaurantId={highlightRestaurantId} />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const SupplierRowHeader = ({
  row,
  vatMode,
  onToggle,
}: {
  row: SupplierPriceEntry;
  vatMode: VatMode;
  onToggle: () => void;
}) => {
  const fresh = row.kind === 'CLOSED' ? freshness(row.closedAt) : freshness(row.lastModifiedAt);
  const subtitle =
    row.kind === 'CLOSED'
      ? `last closed ${fresh?.label ?? ''}`
      : `deal${fresh ? ` · updated ${fresh.label}` : ''}`;

  return (
    <Flex as='button' w='full' textAlign='start' align='center' justify='space-between' bg='gray.50' px='12px' py='10px' onClick={onToggle}>
      <Box minW={0} pr='8px'>
        <Text fontSize='13.5px' fontWeight='semibold' color='gray.800' noOfLines={1}>
          {row.supplierName ?? `Supplier #${row.supplierId}`}
        </Text>
        <Text fontSize='11.5px' color='gray.500' noOfLines={1}>
          {subtitle}
        </Text>
      </Box>
      <Flex align='center' flexShrink={0}>
        {row.isBest && (
          <Box bg='green.50' border='1px solid' borderColor='green.200' px='8px' py='1px' borderRadius='full' mr='8px'>
            <Text fontSize='10px' fontWeight='bold' letterSpacing='0.08em' color='green.600'>
              BEST
            </Text>
          </Box>
        )}
        {row.comparablePrice != null ? (
          <Text fontSize='15px' fontWeight='bold' color='gray.900' whiteSpace='nowrap'>
            AED {formatPrice(withVat(row.comparablePrice, vatMode))}
          </Text>
        ) : (
          <Box textAlign='end'>
            <Text fontSize='15px' fontWeight='bold' color='gray.900' whiteSpace='nowrap'>
              AED {formatPrice(withVat(row.price, vatMode))} / {row.unit}
            </Text>
            <Text fontSize='10px' color='#B26B00'>
              unit differs
            </Text>
          </Box>
        )}
      </Flex>
    </Flex>
  );
};

const HistoryTable = ({
  row,
  ladder,
  vatMode,
  highlightRestaurantId,
}: {
  row: SupplierPriceEntry;
  ladder: UomLadder;
  vatMode: VatMode;
  highlightRestaurantId?: number;
}) => (
  <Box px='12px' py='8px'>
    <Flex fontSize='10px' fontWeight='semibold' letterSpacing='0.08em' color='gray.500' mb='4px'>
      <Text flex='2'>RESTAURANT</Text>
      <Text flex='1' textAlign='end'>
        VOL
      </Text>
      <Text flex='1' textAlign='end'>
        PUBLIC
      </Text>
      <Text flex='1.2' textAlign='end'>
        CLOSED
      </Text>
      <Text flex='1' textAlign='end'>
        DISC
      </Text>
    </Flex>

    {row.history.map((entry, index) => {
      const fresh = freshness(entry.closedAt);
      const disc = historyDiscountPct(entry, row.publicPrice, row.publicPriceUnit, ladder);
      const entryUnit = tierUnit(ladder, entry.priceUnit);
      const isMine = highlightRestaurantId != null && entry.restaurantId === highlightRestaurantId;

      return (
        <Flex
          key={`${entry.restaurantId}-${entry.closedAt}-${index}`}
          align='center'
          py='6px'
          px='4px'
          mx='-4px'
          borderRadius='6px'
          bg={isMine ? 'red.50' : 'transparent'}
          borderTop={index > 0 ? '1px solid' : 'none'}
          borderTopColor='gray.100'>
          <Box flex='2' minW={0}>
            <Text fontSize='12.5px' fontWeight={isMine ? 'bold' : 'medium'} color='gray.800' noOfLines={1}>
              {entry.restaurantName ?? `Restaurant #${entry.restaurantId}`}
            </Text>
            <Text fontSize='10.5px' color='gray.500'>
              {fresh?.label ?? ''}
            </Text>
          </Box>
          <Text flex='1' textAlign='end' fontSize='12.5px' color='gray.700'>
            {entry.quantity ?? '—'}
          </Text>
          <Text flex='1' textAlign='end' fontSize='12.5px' color='gray.400'>
            {row.publicPrice != null ? formatPrice(withVat(row.publicPrice, vatMode)) : '—'}
          </Text>
          <Box flex='1.2' textAlign='end'>
            <Text fontSize='12.5px' fontWeight='bold' color='gray.900'>
              {formatPrice(withVat(entry.price, vatMode))}
            </Text>
            {entryUnit && entryUnit !== row.unit && (
              <Text fontSize='10px' color='gray.500'>
                / {entryUnit}
              </Text>
            )}
          </Box>
          <Text flex='1' textAlign='end' fontSize='12.5px' fontWeight='semibold' color={disc != null && disc < 0 ? 'green.600' : 'gray.500'}>
            {disc != null ? `${disc > 0 ? '+' : ''}${disc.toFixed(0)}%` : '—'}
          </Text>
        </Flex>
      );
    })}
  </Box>
);
